import { useState, useEffect, useCallback } from 'react';

/**
 * Hook that syncs a local selectedProperty with the global PropertyContext.
 * When the global property changes (via sidebar), this hook updates the local state,
 * which triggers re-fetch in pages that depend on selectedProperty.
 */
export function useGlobalProperty(properties) {
  const [selectedProperty, setSelectedProperty] = useState(() => {
    return localStorage.getItem('eden_selected_property') || null;
  });

  // When properties load, validate stored selection
  useEffect(() => {
    if (!properties?.length) return;
    const stored = localStorage.getItem('eden_selected_property');
    if (stored && properties.some(p => p.id === stored)) {
      setSelectedProperty(stored);
    } else {
      const firstId = properties[0].id;
      setSelectedProperty(firstId);
      localStorage.setItem('eden_selected_property', firstId);
    }
  }, [properties]);

  // Listen for global property changes from sidebar or other pages
  useEffect(() => {
    const handler = () => {
      const stored = localStorage.getItem('eden_selected_property');
      if (stored) {
        setSelectedProperty(prev => {
          if (prev !== stored) return stored;
          return prev;
        });
      }
    };
    window.addEventListener('eden_property_changed', handler);
    // Also listen for storage changes from other tabs
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('eden_property_changed', handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  // When local changes, sync to global
  const selectProperty = useCallback((id) => {
    setSelectedProperty(id);
    localStorage.setItem('eden_selected_property', id);
    window.dispatchEvent(new Event('eden_property_changed'));
  }, []);

  return [selectedProperty, selectProperty];
}
